import { motion, AnimatePresence } from "framer-motion";
import { useBingoGameStore } from "@/plugins/bingo/state/bingoGameStore";

/**
 * CurrentPreviousMirror: shows the current called number (large) and
 * the previous one (small). This mirrors what the Projector shows.
 * Fully cinematic reveal effects are added to the Projector in Step 7 —
 * here the Host sees a simple, snappy version.
 */
export default function CurrentPreviousMirror() {
  const called = useBingoGameStore((s) => s.called);
  const current = called.length ? called[called.length - 1].value : null;
  const previous = called.length > 1 ? called[called.length - 2].value : null;

  return (
    <div className="w-full flex items-end gap-4 mb-4">
      <div
        className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)] mb-2 w-24"
        data-testid="host-current-label"
      >
        Current
      </div>

      <div
        data-testid="host-current-value"
        className="ic-font-display relative rounded-lg border flex items-center justify-center overflow-hidden"
        style={{
          width: 148,
          height: 148,
          background: "var(--ic-bg-elevated)",
          borderColor: "var(--ic-border-subtle)",
        }}
      >
        <AnimatePresence mode="popLayout">
          {current !== null ? (
            <motion.span
              key={current}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.2, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              className="text-7xl font-black"
              style={{
                color: "#fff",
                textShadow: "0 0 24px var(--ic-primary-glow)",
              }}
            >
              {current}
            </motion.span>
          ) : (
            <span
              key="empty"
              className="text-4xl font-black"
              style={{ color: "var(--ic-text-muted)" }}
            >
              —
            </span>
          )}
        </AnimatePresence>
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(120px 80px at 50% 50%, var(--ic-primary-glow), transparent 70%)",
            opacity: current === null ? 0 : 0.6,
          }}
        />
      </div>

      <div
        className="ic-font-display rounded-lg border flex flex-col items-center justify-center"
        style={{
          width: 96,
          height: 96,
          background: "var(--ic-bg-elevated)",
          borderColor: "var(--ic-border-subtle)",
        }}
      >
        <span
          className="text-[9px] tracking-[0.3em] uppercase mb-1"
          style={{ color: "var(--ic-text-muted)" }}
        >
          Previous
        </span>
        <span
          data-testid="host-previous-value"
          className="text-3xl font-bold"
          style={{ color: "var(--ic-text-secondary)" }}
        >
          {previous ?? "—"}
        </span>
      </div>
    </div>
  );
}
